import React, { useEffect, useRef, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import DuplicatePlanDialog from "@/components/admin/DuplicatePlanDialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Sparkles, Plus, X, Upload, FileText, ArrowLeft, Save, Loader2, CheckCircle2, RotateCw, Eye, Wand2, Package as PackageIcon, Copy, Info } from "lucide-react";
import { DELIVERABLE_OPTIONS, PACKAGE_LABEL, PACKAGE_TAGLINE, PACKAGE_TIERS, PREMIUM_TIERS, FLOOR_PLAN_TIERS, isCustomizeTier } from "@/lib/packages";

// Iter49 — Styles list now fetched dynamically from /api/styles (Styles Master).
// STYLES kept as fallback for the tiny window before the network responds.
const STYLES_FALLBACK = ["Modern", "Contemporary", "Ranch", "Traditional", "Farmhouse", "Craftsman", "Colonial", "Mediterranean"];

const DRAWING_TYPES = [
  // Iter60A — Floor-Plan-specific types placed FIRST so admins default
  // to the colored floor plan (which becomes the hero on /floor-plans).
  { value: "floor_plan_colored", label: "Floor Plan — Colored" },
  { value: "floor_plan_bw", label: "Floor Plan — Black & White" },
  { value: "floor_plan", label: "Floor Plan" },
  { value: "foundation_plan", label: "Foundation Plan" },
  { value: "roof_plan", label: "Roof Plan" },
  { value: "site_plan", label: "Site Plan" },
  { value: "exterior_elevation", label: "Exterior Elevation" },
  { value: "interior_elevation", label: "Interior Elevation" },
  { value: "building_section", label: "Building Section" },
  { value: "construction_detail", label: "Construction Detail" },
  { value: "electrical", label: "Electrical" },
  { value: "plumbing", label: "Plumbing" },
  { value: "hvac", label: "HVAC" },
  { value: "schedule", label: "Schedule" },
  { value: "other", label: "Other" },
];

const emptyForm = {
  title: "",
  title_mode: "manual",
  // Iter60A — sales channel: premium (Browse Plans), floor_plan (Floor Plans),
  // or both. Existing plans default to "premium" via server-side migration.
  sales_channel: "premium",
  project_id: "",           // auto-generated on save
  style: "Modern",
  bedrooms: 3,
  bathrooms: 2,
  sqft: 2000,
  stories: 1,
  garage: 2,
  price_usd: 495,
  price_inr: 41500,
  description: "",
  short_description: "",
  features: [],
  images: [],
  floor_plan_images: [],
  technical_drawings: [],   // typed drawings: [{url, type, label, order}]
  videos: [],               // [{url, poster_url, source, mime, duration_s, size_bytes, label}]
  pdf_url: "",
  featured: false,
  status: "draft",
  coming_soon: false,
  launch_date: "",
  // Iter48 — Product Packages (PDF / CAD / Revit) for Premium.
  // Iter60A — Extended with fp_pdf/fp_cad/fp_revit/fp_customize for Floor Plans.
  //           Both groups price independently.
  packages: {
    pdf:          { enabled: false, price_usd: 0, price_inr: 0, override_inr: false, description: "" },
    cad:          { enabled: false, price_usd: 0, price_inr: 0, override_inr: false, description: "" },
    revit:        { enabled: false, price_usd: 0, price_inr: 0, override_inr: false, description: "" },
    fp_pdf:       { enabled: false, price_usd: 0, price_inr: 0, override_inr: false, description: "" },
    fp_cad:       { enabled: false, price_usd: 0, price_inr: 0, override_inr: false, description: "" },
    fp_revit:     { enabled: false, price_usd: 0, price_inr: 0, override_inr: false, description: "" },
    fp_customize: { enabled: false, price_usd: 0, price_inr: 0, override_inr: false, description: "" },
  },
  deliverables: [],
  internal_reference: "",
};

const AdminPlanForm = ({ mode }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [urlSearch] = useSearchParams();
  // Iter60A — When admin navigates from "House Plans → Floor Plans → New Floor
  // Plan", we preselect the floor_plan channel + adjust the workflow header
  // and package sections so the team never has to know about the underlying
  // shared engine.
  const initialChannel = mode === "create" && urlSearch.get("channel") === "floor_plan"
    ? "floor_plan" : "premium";
  // Iter60A-QA — Preselect "Floor Plans" as the primary deliverable so
  // production staff never have to remember to tick it.
  const initialDeliverables = initialChannel === "floor_plan" ? ["Floor Plans"] : [];
  const [form, setForm] = useState(() => ({ ...emptyForm, sales_channel: initialChannel, deliverables: initialDeliverables }));
  // Iter49 — Dynamic style list from Styles Master + Project ID preview.
  const [stylesList, setStylesList] = useState(STYLES_FALLBACK);
  const [stylesByName, setStylesByName] = useState({}); // {name: {prefix, ...}}
  const [nextProjectIdPreview, setNextProjectIdPreview] = useState("");
  useEffect(() => {
    api.get("/styles/detailed").then(({ data }) => {
      const items = data.items || [];
      if (items.length) {
        setStylesList(items.map((s) => s.name));
        setStylesByName(Object.fromEntries(items.map((s) => [s.name, s])));
      }
    }).catch(() => { /* keep fallback */ });
  }, []);

  // Iter49 — Fetch a live Project ID preview whenever the selected Style
  // changes AND the plan is either new or still a Draft (published plans
  // have their IDs locked and won't regenerate).
  // Iter60A-QA — Floor Plans use their own DH-FP-##### sequence (style
  // irrelevant); pass sales_channel so the preview API returns the right one.
  useEffect(() => {
    const status = (form.status || "").toLowerCase();
    const channel = (form.sales_channel || "premium").toLowerCase();
    if (form.project_id && status === "published") { setNextProjectIdPreview(""); return; }
    // Premium/both require style before we can preview an ID; floor_plan does not.
    if (channel !== "floor_plan" && !form.style) { setNextProjectIdPreview(""); return; }
    let cancelled = false;
    api.get("/admin/plans/next-project-id", { params: {
      style: form.style || undefined,
      sales_channel: channel,
    }})
      .then(({ data }) => { if (!cancelled) setNextProjectIdPreview(data.next_project_id || ""); })
      .catch(() => { if (!cancelled) setNextProjectIdPreview(""); });
    return () => { cancelled = true; };
  }, [form.style, form.status, form.project_id, form.sales_channel]);
  const [loading, setLoading] = useState(mode === "edit");
  const [imageInput, setImageInput] = useState("");
  const [featureInput, setFeatureInput] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  // Iter45 — per-category upload state; targetKey → {active,total,done,failed,running}.
  const [uploadQueues, setUploadQueues] = useState({});
  const isUploading = (key) => !!(uploadQueues[key]?.active);
  // Per-URL variant-generation status polled from backend.
  // Shape: { [url]: { ready, total, done } }
  const [variantStatus, setVariantStatus] = useState({});
  // Plan-level processing status: { status: 'processing'|'ready'|'failed', stage, ready, total, percent }
  const [planProcessing, setPlanProcessing] = useState(null);
  const imageFileRef = useRef(null);
  const floorFileRef = useRef(null);
  const videoFileRef = useRef(null);
  const pdfFileRef = useRef(null);

  // Plan-level processing poll — only in edit mode after save.
  // Runs for up to 5 minutes (200 × 1.5s) so a slow variant precompute
  // still updates the banner. The status endpoint self-heals on the
  // backend, so simply polling long enough is often enough to fix a
  // stuck plan without any admin action.
  useEffect(() => {
    if (mode !== "edit" || !id) return;
    let cancelled = false;
    let rounds = 0;
    const poll = async () => {
      if (cancelled) return;
      rounds += 1;
      try {
        const { data } = await api.get(`/admin/plans/${id}/processing-status`);
        if (cancelled) return;
        setPlanProcessing(data);
        if (data.status === "ready" || data.status === "failed") return;
        if (rounds >= 200) return;  // 200 × 1.5s ≈ 5 minutes
        setTimeout(poll, 1500);
      } catch (e) {
        // Best-effort polling — log to debug only so QA console stays clean.
        // [removed console.debug]
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [id, mode, planProcessing?.status === "processing" ? 0 : 1]);  // re-arm if user clicks Refresh

  // Poll background variant-generation progress for uploaded images.
  // Renderings (form.images) → kind=rendering; drawings → kind=drawing.
  // Stops polling a URL once it reports done. Polls every 1.5s until done
  // or after 12 rounds (~18s) to avoid runaway polling.
  useEffect(() => {
    const renderingUrls = (form.images || []).filter(Boolean);
    const drawingUrls = (form.technical_drawings || []).map((d) => d?.url).filter(Boolean);
    if (renderingUrls.length === 0 && drawingUrls.length === 0) return;

    let cancelled = false;
    let rounds = 0;

    const poll = async () => {
      if (cancelled) return;
      rounds += 1;
      try {
        const [r1, r2] = await Promise.all([
          renderingUrls.length
            ? api.get("/admin/media/variant-status", { params: { urls: renderingUrls.join(","), kind: "rendering" } })
            : Promise.resolve({ data: {} }),
          drawingUrls.length
            ? api.get("/admin/media/variant-status", { params: { urls: drawingUrls.join(","), kind: "drawing" } })
            : Promise.resolve({ data: {} }),
        ]);
        if (cancelled) return;
        setVariantStatus((prev) => ({ ...prev, ...(r1.data || {}), ...(r2.data || {}) }));
        // Stop polling once every URL is done, or after ~18s.
        const merged = { ...(r1.data || {}), ...(r2.data || {}) };
        const allDone = [...renderingUrls, ...drawingUrls].every((u) => merged[u]?.done);
        if (allDone) return;
        if (rounds >= 12) return;
        setTimeout(poll, 1500);
      } catch (e) {
        // Best-effort polling — quiet log so we can spot systemic failures.
        // [removed console.debug]
      }
    };

    // Kick off with a short delay so background task has a moment to start.
    const t = setTimeout(poll, 400);
    return () => { cancelled = true; clearTimeout(t); };
  }, [form.images, form.technical_drawings]);

  useEffect(() => {
    if (mode !== "edit" || !id) return;
    (async () => {
      try {
        // Use admin plans endpoint filter to include drafts/archived
        const { data } = await api.get("/admin/plans", { params: { limit: 200 } });
        const found = data.items.find((p) => p.id === id);
        if (!found) {
          toast.error("Plan not found");
          navigate("/admin/plans");
          return;
        }
        setForm({
          ...emptyForm,
          ...found,
          pdf_url: found.pdf_url || "",
          // Iter48 — deep-merge packages so any missing tier keeps default
          // (avoids overwriting emptyForm defaults when the field is absent
          // on legacy plans created before Iter48).
          packages: {
            ...emptyForm.packages,
            ...(found.packages || {}),
          },
          deliverables: Array.isArray(found.deliverables) ? found.deliverables : [],
          internal_reference: found.internal_reference || "",
          // Auto-migrate legacy floor_plan_images → technical_drawings on edit
          technical_drawings: (found.technical_drawings && found.technical_drawings.length)
            ? found.technical_drawings
            : (found.floor_plan_images || []).map((u, i) => ({
                url: u,
                type: "floor_plan",
                label: `Floor Plan ${i + 1}`,
                order: i,
              })),
        });
        // Iter55f — hydrate AI Content Manager state from the plan doc so
        // approvals persist across page reloads.
        if (found.ai_analysis) {
          setVisionInsights(found.ai_analysis);
          setVisionInsightsOpen(true);
        }
        if (found.ai_approvals) {
          setAiApprovals(found.ai_approvals);
        }
        if (found.ai_approvals_inherited_from) {
          setAiApprovalsInheritedFrom(found.ai_approvals_inherited_from);
        }
      } catch (e) {
        toast.error("Failed to load plan", { description: e.response?.data?.detail || "Try again." });
      } finally {
        setLoading(false);
      }
    })();
  }, [mode, id, navigate]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Iter45 — Independent per-category upload with concurrency=3.
  // Each media section (images / technical_drawings / videos / pdf_url)
  // maintains its own queue. Uploading Renders NEVER pauses Floor Plans.
  const UPLOAD_CONCURRENCY = 3;
  const uploadFiles = async (files, kind, targetKey) => {
    if (!files || files.length === 0) return;
    const backendUrl = process.env.REACT_APP_BACKEND_URL || "(missing REACT_APP_BACKEND_URL)";
    const filesArr = Array.from(files);
    // Merge into any in-flight queue for this targetKey — new files simply
    // extend the existing queue, they do not restart it.
    setUploadQueues((q) => ({
      ...q,
      [targetKey]: {
        active: true,
        total: (q[targetKey]?.total || 0) + filesArr.length,
        done: q[targetKey]?.done || 0,
        failed: q[targetKey]?.failed || 0,
        running: q[targetKey]?.running || 0,
      },
    }));

    // Worker pool bounded to UPLOAD_CONCURRENCY.
    let cursor = 0;
    const uploaded = [];
    const uploadOne = async () => {
      while (true) {
        const idx = cursor++;
        if (idx >= filesArr.length) return;
        const file = filesArr[idx];
        setUploadQueues((q) => ({ ...q, [targetKey]: {
          ...q[targetKey], running: (q[targetKey]?.running || 0) + 1,
        }}));
        try {
          // [removed console.info]
          const fd = new FormData();
          fd.append("file", file);
          const { data } = await api.post(`/uploads?kind=${kind}`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          uploaded.push({ idx, data });
          setUploadQueues((q) => ({ ...q, [targetKey]: {
            ...q[targetKey],
            done: (q[targetKey]?.done || 0) + 1,
            running: Math.max(0, (q[targetKey]?.running || 0) - 1),
          }}));
        } catch (e) {
          console.error("[Upload FAILED]", { target: `${backendUrl}/api/uploads?kind=${kind}`, status: e.response?.status, detail: e.response?.data?.detail, filename: file.name });
          const detail = e.response?.data?.detail || e.message || "Try again.";
          toast.error(`Upload failed: ${file.name}`, {
            description: `${e.response?.status ? "HTTP " + e.response.status + " — " : ""}${detail}`,
            duration: 10000,
          });
          setUploadQueues((q) => ({ ...q, [targetKey]: {
            ...q[targetKey],
            failed: (q[targetKey]?.failed || 0) + 1,
            running: Math.max(0, (q[targetKey]?.running || 0) - 1),
          }}));
        }
      }
    };
    const workers = Array.from({ length: Math.min(UPLOAD_CONCURRENCY, filesArr.length) }, uploadOne);
    await Promise.all(workers);

    // Persist uploaded results in original submission order.
    const ordered = uploaded.sort((a, b) => a.idx - b.idx).map((u) => u.data);
    if (targetKey === "pdf_url") set("pdf_url", ordered[0]?.url || "");
    else if (targetKey === "technical_drawings") {
      // Iter47 fix — read from freshest state via functional updater so
      // overlapping same-category uploads cannot overwrite each other via
      // a stale `form` closure captured at drop time.
      setForm((f) => {
        const base = f.technical_drawings || [];
        const additions = ordered.map((d, i) => ({
          url: d.url,
          type: "floor_plan",
          // Auto-label follows the Type until manually edited (Iter45 #4).
          label: `Floor Plan ${base.filter((x) => (x.type || "floor_plan") === "floor_plan").length + i + 1}`,
          order: base.length + i,
        }));
        return { ...f, technical_drawings: [...base, ...additions] };
      });
    }
    else if (targetKey === "videos") {
      setForm((f) => {
        const base = f.videos || [];
        const additions = ordered.map((v, i) => ({
          url: v.url, poster_url: v.poster_url || "", source: v.source || "file",
          mime: v.content_type, duration_s: v.duration_s, size_bytes: v.size,
          label: `Walkthrough ${base.length + i + 1}`, order: base.length + i,
        }));
        return { ...f, videos: [...base, ...additions] };
      });
    }
    else setForm((f) => ({ ...f, [targetKey]: [...(f[targetKey] || []), ...ordered.map((d) => d.url)] }));

    // Mark queue inactive if nothing else is in flight.
    setUploadQueues((q) => {
      const s = q[targetKey];
      if (!s) return q;
      const isDone = (s.done + s.failed) >= s.total;
      return isDone ? { ...q, [targetKey]: { ...s, active: false } } : q;
    });
    const okCount = ordered.length;
    if (okCount) toast.success(`${okCount} file${okCount === 1 ? "" : "s"} uploaded to ${targetKey === "technical_drawings" ? "drawings" : targetKey}`);
  };

  const addImage = () => { if (imageInput.trim()) { set("images", [...form.images, imageInput.trim()]); setImageInput(""); } };
  const addFeature = () => { if (featureInput.trim()) { set("features", [...form.features, featureInput.trim()]); setFeatureInput(""); } };

  // Iter45 — AI Generate now uses a background job (POST returns job_id,
  // GET polls). This eliminates the HTTP 504 seen on production when
  // Claude took longer than the ~30 s ingress timeout.
  const aiPollRef = useRef(null);
  const stopAiPoll = () => {
    if (aiPollRef.current) { clearInterval(aiPollRef.current); aiPollRef.current = null; }
  };
  useEffect(() => () => stopAiPoll(), []);

  const runAi = async () => {
    if (form.title_mode === "manual" && !form.title.trim()) {
      toast.error("Add a title first");
      return;
    }
    const backendUrl = process.env.REACT_APP_BACKEND_URL || "(missing REACT_APP_BACKEND_URL)";
    setAiLoading(true);
    stopAiPoll();
    try {
      const { data } = await api.post("/ai/generate-description", {
        title: form.title_mode === "auto" ? "" : form.title,
        style: form.style,
        bedrooms: Number(form.bedrooms), bathrooms: Number(form.bathrooms),
        sqft: Number(form.sqft), stories: Number(form.stories),
        garage: Number(form.garage), features: form.features,
      });
      const jobId = data.job_id;
      const finalizeSuccess = (r) => {
        setForm((f) => ({
          ...f,
          title: (f.title_mode === "auto" && r.title) ? r.title : f.title,
          short_description: r.short_description || f.short_description,
          description: r.description || f.description,
          features: r.features && r.features.length ? r.features : f.features,
        }));
        toast.success(form.title_mode === "auto" && r.title
          ? `Generated: "${r.title}"`
          : "AI description generated");
        setAiLoading(false);
      };
      // Poll every 2 s
      aiPollRef.current = setInterval(async () => {
        try {
          const { data: s } = await api.get(`/ai/generate-description/${jobId}`);
          if (s.status === "completed" && s.result) {
            stopAiPoll();
            finalizeSuccess(s.result);
          } else if (s.status === "failed") {
            stopAiPoll();
            setAiLoading(false);
            toast.error("AI generation failed", { description: s.error || "Unknown error", duration: 12000 });
          }
        } catch (e) {
          stopAiPoll();
          setAiLoading(false);
          toast.error("AI generation poll failed", { description: e.message });
        }
      }, 2000);
    } catch (e) {
      setAiLoading(false);
      const detail = e.response?.data?.detail || e.message || "Try again.";
      const status = e.response?.status ? `HTTP ${e.response.status} — ` : "";
      toast.error("Could not start AI Generate", {
        description: `${status}${detail} (Backend: ${backendUrl}.)`,
        duration: 12000,
      });
    }
  };

  // -----------------------------------------------------------
  // AI Vision Analyze — background job polling (Iter43)
  // -----------------------------------------------------------
  const [visionJob, setVisionJob] = useState(null); // {jobId, status, progress, stage, eta_seconds, error, result}
  const [visionInsights, setVisionInsights] = useState(null); // completed result
  const [visionInsightsOpen, setVisionInsightsOpen] = useState(false);
  const [pendingStyleSwap, setPendingStyleSwap] = useState(null); // {from, to, confidence, rationale}
  // Iter55f — AI Content Manager approvals (per-field publish gates)
  const [aiApprovals, setAiApprovals] = useState({});
  const [aiApprovalsSaving, setAiApprovalsSaving] = useState(false);
  // Iter58 — Currency Management. Load the current USD→INR rate so the
  // package editor can show live-computed INR next to each USD input.
  // Admin can flip "Override INR" to lock a custom price on any tier.
  const [inrRate, setInrRate] = useState(83.5);
  useEffect(() => {
    api.get("/settings/public").then(({ data }) => {
      const r = (data?.currency?.rates?.INR) || data?.currency?.usd_to_inr_rate;
      if (r && r > 0) setInrRate(Number(r));
    }).catch(() => {});
  }, []);

  // Iter55f — inherited AI approvals banner (set on Iter56 duplicates)
  const [aiApprovalsInheritedFrom, setAiApprovalsInheritedFrom] = useState(null);
  // Iter56 — Duplicate Plan Dialog opener state
  const [duplicateOpen, setDuplicateOpen] = useState(false);
  const visionPollRef = useRef(null);
  const visionStartedAtRef = useRef(null);

  const stopVisionPoll = () => {
    if (visionPollRef.current) {
      clearInterval(visionPollRef.current);
      visionPollRef.current = null;
    }
  };

  useEffect(() => () => stopVisionPoll(), []);

  const applyVisionResult = (r, opts = {}) => {
    // Auto-fill form fields (Priority 3c per user)
    setForm((f) => ({
      ...f,
      title: (f.title_mode === "auto" && r.project_name) ? r.project_name : (f.title || r.project_name || ""),
      short_description: r.short_description || f.short_description,
      description: r.description || f.description,
      features: r.features && r.features.length ? r.features : f.features,
      seo_title: r.seo_title || f.seo_title,
      meta_description: r.meta_description || f.meta_description,
    }));
    // Style — confirm before overwriting
    const suggested = r.suggested_style;
    if (suggested && stylesList.includes(suggested) && suggested !== form.style && !opts.skipStyle) {
      setPendingStyleSwap({
        from: form.style || "(none)",
        to: suggested,
        confidence: r.style_confidence,
        rationale: r.style_rationale,
      });
    }
    setVisionInsights(r);
    setVisionInsightsOpen(true);
    toast.success("AI Vision analysis applied", { description: "Review the extra insights panel below." });
  };

  const runVision = async () => {
    if (!form.images?.length && !form.technical_drawings?.length) {
      toast.error("Upload at least one render or drawing before running AI Vision.");
      return;
    }
    try {
      const { data } = await api.post("/ai/vision-analyze", {
        plan_id: id || null,
        title: form.title_mode === "manual" ? form.title : "",
        style: form.style,
        bedrooms: Number(form.bedrooms) || null,
        bathrooms: Number(form.bathrooms) || null,
        sqft: Number(form.sqft) || null,
        stories: Number(form.stories) || null,
        garage: Number(form.garage) || null,
        price_usd: Number(form.price_usd) || null,
        price_inr: Number(form.price_inr) || null,
        images: form.images || [],
        technical_drawings: (form.technical_drawings || []).map((d) => ({
          url: d.url,
          // Iter60A-QA(final) — Send BOTH keys so the backend picker (which
          // reads `drawing_type` OR `type` after the fix in _run_vision_analysis)
          // has a canonical value even for older drawings uploaded before the
          // field split. Prefer `drawing_type`, fall back to `type`.
          type: d.type || d.drawing_type || "floor_plan",
          drawing_type: d.drawing_type || d.type || "floor_plan",
          label: d.label || "",
        })),
        // Iter60A-QA — Channel context so the backend routes the drawing to
        // the floor-plan pipeline (rooms/sqft/orientation extraction) rather
        // than the marketing/rendering pipeline when this is a Floor Plan.
        sales_channel: form.sales_channel || "premium",
      });
      visionStartedAtRef.current = Date.now();
      setVisionJob({ jobId: data.job_id, status: "queued", progress: 0,
                     stage: "Queued", eta_seconds: data.eta_seconds });
      toast.info("AI Vision analysis started", {
        description: `~${data.eta_seconds}s. You can keep editing while it runs.`,
      });
      // Poll every 2s
      stopVisionPoll();
      visionPollRef.current = setInterval(async () => {
        try {
          const { data: s } = await api.get(`/ai/vision-analyze/${data.job_id}`);
          setVisionJob(s);
          if (s.status === "completed" && s.result) {
            stopVisionPoll();
            applyVisionResult(s.result);
          } else if (s.status === "failed") {
            stopVisionPoll();
            toast.error("AI Vision failed", { description: s.error || "Unknown error", duration: 12000 });
          }
        } catch (e) {
          stopVisionPoll();
          toast.error("AI Vision poll failed", { description: e.message });
        }
      }, 2000);
    } catch (e) {
      toast.error("Could not start AI Vision", { description: e.response?.data?.detail || e.message });
    }
  };

  const confirmStyleSwap = () => {
    if (pendingStyleSwap) {
      set("style", pendingStyleSwap.to);
      toast.success(`Style set to ${pendingStyleSwap.to}`);
    }
    setPendingStyleSwap(null);
  };

  const dismissStyleSwap = () => setPendingStyleSwap(null);

  const buildPayload = (statusOverride) => {
    // Sync floor_plan_images with technical_drawings (backwards compat + parity)
    const typed = (form.technical_drawings || []).map((d, i) => ({
      url: d.url,
      type: d.type || "floor_plan",
      label: d.label || "",
      order: typeof d.order === "number" ? d.order : i,
    }));
    typed.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    // Iter48 — coerce package prices to numbers before send.
    // Iter58 — persist override_inr flag; price_inr is only meaningful
    // when override_inr=true (server computes virtual INR otherwise).
    const packages = { ...(form.packages || {}) };
    for (const t of ["pdf", "cad", "revit"]) {
      const p = packages[t] || {};
      const overrideInr = Boolean(p.override_inr);
      packages[t] = {
        enabled: !!p.enabled,
        price_usd: Number(p.price_usd) || 0,
        override_inr: overrideInr,
        // Only store a custom price_inr when the admin actually overrode it.
        // When override is off, wipe it to 0 so a stale override doesn't
        // silently override the auto-computed price on the public site.
        price_inr: overrideInr ? (Number(p.price_inr) || 0) : 0,
        description: (p.description || "").trim(),
      };
    }
    return {
      ...form,
      bedrooms: Number(form.bedrooms),
      bathrooms: Number(form.bathrooms),
      sqft: Number(form.sqft),
      stories: Number(form.stories),
      garage: Number(form.garage),
      price_usd: Number(form.price_usd),
      price_inr: Number(form.price_inr),
      status: statusOverride || form.status || "draft",
      // Iter60A — persist sales_channel so backend can filter marketplace visibility.
      sales_channel: form.sales_channel || "premium",
      technical_drawings: typed,
      floor_plan_images: typed.map((t) => t.url), // legacy mirror
      packages,
      deliverables: Array.isArray(form.deliverables) ? form.deliverables : [],
      internal_reference: (form.internal_reference || "").trim() || null,
    };
  };

  const save = async (statusOverride) => {
    setSaving(true);
    try {
      const payload = buildPayload(statusOverride);
      let data;
      if (mode === "edit") {
        const res = await api.put(`/plans/${id}`, payload);
        data = res.data;
      } else {
        const res = await api.post("/plans", payload);
        data = res.data;
      }
      toast.success(mode === "edit" ? "Plan updated" : `Plan ${payload.status}`, { description: data.title });
      navigate("/admin/plans");
    } catch (e) {
      toast.error("Save failed", { description: e.response?.data?.detail || "Try again." });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <AdminLayout title="Loading plan…"><div className="text-muted-foreground">Loading…</div></AdminLayout>;

  return (
    <AdminLayout
      title={mode === "edit"
        ? `Edit · ${form.title || "Plan"}`
        : (form.sales_channel === "floor_plan" ? "Create new Floor Plan" : "Create new plan")}
      actions={
        <div className="flex gap-2">
          {mode === "edit" && id && (
            <Button variant="outline" className="rounded-none" onClick={() => navigate(`/admin/plans/${id}/versions`)} data-testid="view-versions-btn">
              Version history
            </Button>
          )}
          {mode === "edit" && id && (
            <Button variant="outline" className="rounded-none" onClick={() => setDuplicateOpen(true)} data-testid="duplicate-plan-toolbar-btn" title="Create a draft copy of this plan">
              <Copy size={14} className="mr-2" /> Duplicate plan
            </Button>
          )}
          <Button variant="outline" className="rounded-none" onClick={() => navigate("/admin/plans")} data-testid="cancel-plan-btn">
            <ArrowLeft size={14} className="mr-2" /> Back
          </Button>
          <Button variant="outline" className="rounded-none" disabled={saving} onClick={() => save("draft")} data-testid="save-draft-btn">
            <Save size={14} className="mr-2" /> Save draft
          </Button>
          <Button className="rounded-none bg-accent hover:bg-accent/90" disabled={saving} onClick={() => save("published")} data-testid="publish-plan-btn">
            {saving ? "Saving…" : (mode === "edit" ? "Update & publish" : "Publish plan")}
          </Button>
        </div>
      }
    >
      <div className="max-w-4xl space-y-8">
        {/* Iter46 Fix-C — Dual progress: UPLOADING (client → server) is
            separate from PROCESSING (server-side variants). This panel
            aggregates any active per-category uploads at the top and,
            once uploads finish, shows what the server is doing. Admins
            can now tell at a glance whether the wait is network or CPU. */}
        {(() => {
          const activeUploads = Object.entries(uploadQueues).filter(([, s]) => s && s.total > 0);
          const uploadsRunning = activeUploads.some(([, s]) => s.active);
          const anyUpload = activeUploads.length > 0;
          const uploadDone = activeUploads.reduce((n, [, s]) => n + (s.done || 0), 0);
          const uploadFailed = activeUploads.reduce((n, [, s]) => n + (s.failed || 0), 0);
          const uploadTotal = activeUploads.reduce((n, [, s]) => n + (s.total || 0), 0);
          const uploadPct = uploadTotal ? Math.round((uploadDone / uploadTotal) * 100) : 0;
          const processing = planProcessing && planProcessing.status !== "ready" ? planProcessing : null;
          if (!anyUpload && !processing) return null;
          const STAGE_STEPS = [
            { key: "uploading",           label: "Uploading files" },
            { key: "generating_variants", label: "Processing images" },
            { key: "compressing",         label: "Generating variants" },
            { key: "applying_watermark",  label: "Applying watermarks" },
            { key: "verifying",           label: "Finalizing" },
          ];
          const curStage = processing?.stage || (uploadsRunning ? "uploading" : "generating_variants");
          const stageIdx = STAGE_STEPS.findIndex((s) => s.key === curStage);
          return (
            <div data-testid="dual-progress-panel" className="border border-primary/30 bg-primary/[0.03]">
              {/* Row 1 — Uploading */}
              <div className="p-4 border-b border-primary/10 flex items-center gap-4" data-testid="dual-progress-upload-row">
                {uploadsRunning ? (
                  <Loader2 size={16} className="animate-spin text-primary flex-shrink-0" />
                ) : (
                  <CheckCircle2 size={16} className="text-emerald-600 flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-neutral-900 flex items-center gap-2">
                    <span>Uploading files</span>
                    {anyUpload && (
                      <span className="text-xs font-mono text-muted-foreground" data-testid="dual-progress-upload-count">
                        {uploadDone}/{uploadTotal}{uploadFailed > 0 && <span className="text-rose-600"> · {uploadFailed} failed</span>}
                      </span>
                    )}
                    {!uploadsRunning && anyUpload && <span className="text-xs text-emerald-700">✓ Complete</span>}
                  </div>
                  <div className="mt-2 h-1.5 w-full bg-neutral-200 overflow-hidden" data-testid="dual-progress-upload-bar">
                    <div className={`h-full transition-all ${uploadsRunning ? "bg-primary" : "bg-emerald-500"}`}
                         style={{ width: `${anyUpload ? Math.max(4, uploadPct) : 0}%` }} />
                  </div>
                </div>
              </div>

              {/* Row 2 — Processing (server-side) */}
              <div className="p-4 flex items-start gap-4" data-testid="dual-progress-processing-row">
                {processing?.status === "failed" ? (
                  <X size={16} className="text-rose-600 flex-shrink-0 mt-0.5" />
                ) : processing ? (
                  <Loader2 size={16} className="animate-spin text-amber-600 flex-shrink-0 mt-0.5" />
                ) : (
                  <CheckCircle2 size={16} className="text-neutral-300 flex-shrink-0 mt-0.5" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-neutral-900 flex items-center gap-2 flex-wrap" data-testid="plan-processing-stage">
                    <span>Processing images</span>
                    {processing && (
                      <>
                        <span className="text-xs font-mono text-muted-foreground">
                          {processing.ready}/{processing.total} · {processing.percent}%
                        </span>
                        {processing.started_at && (
                          <span className="text-[10px] text-muted-foreground" data-testid="plan-processing-elapsed">
                            · started {new Date(processing.started_at).toLocaleTimeString()}
                          </span>
                        )}
                      </>
                    )}
                  </div>
                  {/* Stage timeline — labels hide below md to avoid
                      overlap on narrow screens; dots + colour still
                      convey progress (Iter46 mobile fix). */}
                  <div className="mt-2 grid grid-cols-5 gap-1 text-[10px] uppercase tracking-wider" data-testid="dual-progress-stage-timeline">
                    {STAGE_STEPS.map((s, i) => {
                      const done = i < stageIdx || (processing?.status === "ready");
                      const active = i === stageIdx && (uploadsRunning || processing);
                      return (
                        <div key={s.key} className="flex items-center gap-1" data-testid={`stage-${s.key}`}>
                          <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${done ? "bg-emerald-500" : active ? "bg-amber-500 animate-pulse" : "bg-neutral-300"}`} />
                          <span className={`hidden md:inline truncate ${done ? "text-emerald-700" : active ? "text-amber-700 font-semibold" : "text-neutral-400"}`}>{s.label}</span>
                        </div>
                      );
                    })}
                  </div>
                  {/* Mobile-only caption — plain text so admins on phones
                      still know which stage is running. */}
                  <div className="mt-1 md:hidden text-[11px] text-muted-foreground" data-testid="dual-progress-mobile-caption">
                    Currently: <span className="text-amber-700 font-semibold">{STAGE_STEPS[Math.max(0, stageIdx)]?.label || "Processing"}</span>
                  </div>
                  {processing && (
                    <>
                      <div className="mt-2 h-1.5 w-full bg-neutral-200 overflow-hidden">
                        <div className={`h-full transition-all ${processing.status === "failed" ? "bg-rose-500" : "bg-amber-500"}`}
                             style={{ width: `${Math.max(4, processing.percent)}%` }} />
                      </div>
                      <div className="mt-1 text-[11px] text-muted-foreground">
                        Plan is hidden from the marketplace until every image variant is on disk.
                      </div>
                    </>
                  )}
                </div>
                {processing && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="rounded-none flex-shrink-0"
                    data-testid="plan-processing-refresh-btn"
                    onClick={async () => {
                      try {
                        const { data } = await api.get(`/admin/plans/${id}/processing-status`);
                        setPlanProcessing(data);
                        if (data.status === "ready") toast.success("All variants optimized · Ready");
                      } catch (e) { toast.error("Unable to fetch status"); }
                    }}
                  >
                    <RotateCw size={13} className="mr-1" /> Refresh
                  </Button>
                )}
              </div>
            </div>
          );
        })()}

        {/* AI Vision progress banner */}
        {visionJob && visionJob.status !== "completed" && visionJob.status !== "failed" && (
          <div
            data-testid="vision-progress-banner"
            className="border border-primary/40 bg-primary/5 p-4 flex items-center gap-4"
          >
            <Loader2 size={18} className="animate-spin text-primary flex-shrink-0" />
            <div className="flex-1">
              <div className="text-sm font-semibold text-neutral-900 flex items-center gap-2" data-testid="vision-progress-stage">
                <Wand2 size={14} className="text-primary" /> AI Vision · {visionJob.stage || visionJob.status}
              </div>
              <div className="mt-1 flex items-center gap-3 text-xs text-muted-foreground">
                <span>{visionJob.progress || 0}%</span>
                <div className="h-1.5 flex-1 max-w-md bg-primary/10">
                  <div className="h-full bg-primary transition-all" style={{ width: `${visionJob.progress || 0}%` }} />
                </div>
                {visionJob.eta_seconds && (
                  <span data-testid="vision-progress-eta">
                    ~{Math.max(0, Math.round(visionJob.eta_seconds - ((Date.now() - (visionStartedAtRef.current || Date.now())) / 1000)))}s remaining
                  </span>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={() => { stopVisionPoll(); setVisionJob(null); toast.info("Analysis dismissed (still running server-side)"); }}
              className="text-xs text-muted-foreground hover:text-foreground"
              data-testid="vision-dismiss-btn"
              aria-label="Dismiss progress"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* Style-swap confirmation */}
        {pendingStyleSwap && (
          <div data-testid="vision-style-confirm" className="border border-accent/40 bg-accent/5 p-4 flex items-start gap-4">
            <Eye size={18} className="text-accent flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="text-sm font-semibold text-neutral-900">
                Change style from <span className="italic">{pendingStyleSwap.from}</span> to <span className="italic">{pendingStyleSwap.to}</span>?
              </div>
              <div className="mt-1 text-xs text-muted-foreground">
                Confidence: {Math.round((pendingStyleSwap.confidence || 0) * 100)}% · {pendingStyleSwap.rationale}
              </div>
            </div>
            <div className="flex gap-2">
              <Button type="button" size="sm" variant="outline" className="rounded-none" onClick={dismissStyleSwap} data-testid="vision-style-keep">Keep {pendingStyleSwap.from}</Button>
              <Button type="button" size="sm" className="rounded-none bg-accent hover:bg-accent/90" onClick={confirmStyleSwap} data-testid="vision-style-swap">Use {pendingStyleSwap.to}</Button>
            </div>
          </div>
        )}

        {/* AI Insights review panel */}
        {visionInsights && visionInsightsOpen && (
          <div data-testid="vision-insights-panel" className="border border-primary/30 bg-neutral-50 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Wand2 size={16} className="text-primary" />
                <div className="overline text-primary">AI Vision Insights</div>
              </div>
              <button
                type="button"
                onClick={() => setVisionInsightsOpen(false)}
                className="text-muted-foreground hover:text-foreground"
                data-testid="vision-insights-close"
                aria-label="Hide insights"
              >
                <X size={16} />
              </button>
            </div>

            <div className="grid md:grid-cols-2 gap-6 text-sm">
              <div data-testid="vision-suggested-style-block">
                <div className="overline text-muted-foreground">Suggested Style</div>
                <div className="font-display text-xl mt-1">
                  {visionInsights.suggested_style || "—"}
                  {typeof visionInsights.style_confidence === "number" && (
                    <span className="ml-2 text-xs font-mono text-muted-foreground">
                      {Math.round(visionInsights.style_confidence * 100)}% confidence
                    </span>
                  )}
                </div>
                {visionInsights.style_rationale && (
                  <p className="mt-2 text-muted-foreground italic">"{visionInsights.style_rationale}"</p>
                )}
              </div>

              <div>
                <div className="overline text-muted-foreground">Suitable For</div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {(visionInsights.best_for || []).map((tag) => (
                    <span key={tag} className="text-xs px-2 py-1 border border-border bg-white">{tag}</span>
                  ))}
                </div>
                {visionInsights.suitable_family_size && (
                  <div className="mt-3 text-xs text-muted-foreground">
                    Family size: <span className="text-foreground">{visionInsights.suitable_family_size}</span>
                  </div>
                )}
              </div>

              {visionInsights.highlights?.length > 0 && (
                <div className="md:col-span-2">
                  <div className="overline text-muted-foreground">Project Highlights</div>
                  <ul className="mt-2 space-y-1.5 list-disc list-inside text-neutral-800">
                    {visionInsights.highlights.map((h, i) => <li key={i}>{h}</li>)}
                  </ul>
                </div>
              )}

              {visionInsights.search_keywords?.length > 0 && (
                <div className="md:col-span-2">
                  <div className="overline text-muted-foreground">Search Keywords</div>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {visionInsights.search_keywords.map((k, i) => (
                      <span key={i} className="text-xs px-2 py-0.5 bg-white border border-border font-mono">{k}</span>
                    ))}
                  </div>
                </div>
              )}

              {visionInsights.suggested_tags?.length > 0 && (
                <div className="md:col-span-2">
                  <div className="overline text-muted-foreground">Suggested Admin Tags</div>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {visionInsights.suggested_tags.map((t, i) => (
                      <span key={i} className="text-xs px-2 py-0.5 bg-accent/10 text-accent border border-accent/30 font-mono">{t}</span>
                    ))}
                  </div>
                </div>
              )}

              {visionInsights.room_schedule?.length > 0 && (
                <div className="md:col-span-2">
                  <div className="overline text-muted-foreground">Room Schedule (from floor plan)</div>
                  <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5 text-neutral-800">
                    {visionInsights.room_schedule.map((r, i) => (
                      <div key={i} className="flex items-baseline gap-2 border-b border-border/60 py-1">
                        <span className="font-semibold whitespace-nowrap">{r.name}</span>
                        {r.notes && <span className="text-xs text-muted-foreground">— {r.notes}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {visionInsights.title_change_rationale && (
                <div className="md:col-span-2 border border-amber-200 bg-amber-50 p-3 text-xs">
                  <span className="font-semibold uppercase tracking-widest text-amber-900">Title suggestion</span>{" "}
                  <span className="text-neutral-800">{visionInsights.title_change_rationale}</span>
                </div>
              )}
            </div>

            {/* Iter55f — AI Content Manager · per-field publish approvals.
                Nothing here is auto-published. Admin must explicitly tick
                the box AND click "Save approvals" for a field to surface
                on the public plan detail page. */}
            {id && aiApprovalsInheritedFrom && (
              <div className="mt-6 border-l-4 border-amber-500 bg-amber-50 p-4 flex items-start gap-3" data-testid="ai-approvals-inherited-banner">
                <Info size={18} className="text-amber-700 shrink-0 mt-0.5" />
                <div className="flex-1">
                  <div className="text-sm font-semibold text-amber-900">AI approvals inherited from source plan</div>
                  <p className="text-xs text-amber-800 mt-1">
                    These approvals were carried over from the plan you duplicated. Please review the AI-generated content and re-tick / untick as needed before publishing this draft.
                  </p>
                </div>
                <button
                  type="button"
                  className="text-[11px] uppercase tracking-[0.14em] text-amber-900 underline underline-offset-2"
                  onClick={() => setAiApprovalsInheritedFrom(null)}
                  data-testid="ai-approvals-inherited-dismiss"
                >Dismiss</button>
              </div>
            )}
            {id && (
              <AiContentApprovalPanel
                planId={id}
                insights={visionInsights}
                approvals={aiApprovals}
                onApprovalsChange={setAiApprovals}
                saving={aiApprovalsSaving}
                setSaving={setAiApprovalsSaving}
              />
            )}
          </div>
        )}

        {/* Old single-row processing banner replaced by the dual-progress
            panel above (Iter46 Fix-C). The Retry button on failure now
            lives inside the dual panel's processing row via a shared
            handler. */}
        {planProcessing && planProcessing.status === "failed" && id && (
          <div className="border border-rose-200 bg-rose-50 p-3 flex items-center gap-2 text-sm text-rose-800">
            <X size={16} className="text-rose-600" />
            <span className="font-semibold">Processing failed</span>
            <Button
              size="sm"
              variant="outline"
              className="rounded-none border-rose-300 text-rose-700 hover:bg-rose-100 ml-auto"
              data-testid="plan-reprocess-btn"
              onClick={async () => {
                try {
                  await api.post(`/admin/plans/${id}/reprocess`);
                  toast.success("Reprocessing started");
                  setPlanProcessing({ ...planProcessing, status: "processing", stage: "generating_variants" });
                } catch (e) { toast.error("Failed to restart processing"); }
              }}
            >
              Retry
            </Button>
          </div>
        )}
        {planProcessing && planProcessing.status === "ready" && (
          <div className="border border-emerald-200 bg-emerald-50 p-3 flex items-center gap-2 text-sm text-emerald-800" data-testid="plan-processing-ready">
            <CheckCircle2 size={16} className="text-emerald-600" />
            <span className="font-semibold">All images optimized · Ready for marketplace</span>
            <span className="text-xs font-mono text-emerald-700/70 ml-auto">{planProcessing.total} variants cached</span>
          </div>
        )}

        {/* Basic */}
        <div className="border border-border bg-card p-8">
          <div className="overline text-accent mb-6">Basic info</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Project ID — auto-generated, always read-only. Shown even for new plans as "Auto Generated". */}
            <div className="md:col-span-2 flex items-end gap-4 flex-wrap">
              <div className="flex-1 min-w-[220px]">
                <Label className="overline text-muted-foreground">Project ID</Label>
                <div className="rounded-none mt-2 h-12 border border-border bg-muted/60 px-4 flex items-center font-mono text-sm select-all" data-testid="plan-project-id">
                  {form.project_id || <span className="italic text-muted-foreground">Auto-generated on save</span>}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {form.project_id && (form.status || "").toLowerCase() === "published"
                    ? "Permanent identifier — locked because the plan is published. Style changes will NOT change this ID."
                    : "Permanent identifier — assigned once at save. Used across CRM, orders, quotes, and downloads."}
                </p>
              </div>
              {/* Iter49 (Item #11) — Next Project ID preview chip. Live-refetched
                  from /api/admin/plans/next-project-id whenever the Style dropdown
                  changes AND the plan is either new or still a draft (never for
                  published plans since IDs are locked). */}
              {(!form.project_id || (form.status || "").toLowerCase() !== "published") && (
                <div data-testid="next-project-id-chip">
                  <Label className="overline text-muted-foreground">Next Project ID</Label>
                  <div className="rounded-none mt-2 h-12 border border-accent/40 bg-accent/5 px-4 flex items-center font-mono text-sm text-accent">
                    {nextProjectIdPreview
                      ? nextProjectIdPreview
                      : `DH-${(stylesByName[form.style]?.prefix || "??")}-XXXXX`}
                  </div>
                </div>
              )}
            </div>

            {/* Title Mode radio */}
            <div className="md:col-span-2 border-t border-border pt-6">
              <Label className="overline text-muted-foreground">Title mode</Label>
              <div className="mt-3 flex flex-col md:flex-row gap-4" data-testid="title-mode-group">
                <label className={`flex items-start gap-3 border p-3 cursor-pointer flex-1 transition ${form.title_mode === "auto" ? "border-accent bg-accent/5" : "border-border hover:border-neutral-400"}`}>
                  <input
                    type="radio"
                    name="title_mode"
                    value="auto"
                    checked={form.title_mode === "auto"}
                    onChange={() => set("title_mode", "auto")}
                    className="mt-1"
                    data-testid="title-mode-auto"
                  />
                  <div>
                    <div className="text-sm font-semibold">Auto-generate</div>
                    <div className="text-xs text-muted-foreground mt-1">Uses <span className="font-mono">Style — Project ID</span> (e.g. “Contemporary — DH-CT-000001”). Recommended for uniform SKU-style catalogs.</div>
                  </div>
                </label>
                <label className={`flex items-start gap-3 border p-3 cursor-pointer flex-1 transition ${form.title_mode === "manual" ? "border-accent bg-accent/5" : "border-border hover:border-neutral-400"}`}>
                  <input
                    type="radio"
                    name="title_mode"
                    value="manual"
                    checked={form.title_mode === "manual"}
                    onChange={() => set("title_mode", "manual")}
                    className="mt-1"
                    data-testid="title-mode-manual"
                  />
                  <div>
                    <div className="text-sm font-semibold">Manual</div>
                    <div className="text-xs text-muted-foreground mt-1">Marketing-friendly (e.g. “The Northlight”, “Luxury Farmhouse”). Shown on the marketplace.</div>
                  </div>
                </label>
              </div>
            </div>

            {/* Title input — auto-preview vs manual editable */}
            <div className="md:col-span-2">
              <Label className="overline text-muted-foreground">
                {form.title_mode === "auto" ? "Auto-generated title" : "Marketing title"}
              </Label>
              {form.title_mode === "auto" ? (
                <div
                  className="rounded-none mt-2 min-h-12 border border-border bg-muted/60 px-4 py-3 flex items-center text-sm"
                  data-testid="plan-title-preview"
                >
                  {form.title
                    ? <span className="font-semibold" data-testid="plan-title-preview-value">{form.title}</span>
                    : <span className="text-muted-foreground italic">Click <span className="font-semibold text-foreground">AI Generate</span> below to name this plan</span>
                  }
                </div>
              ) : (
                <Input
                  data-testid="plan-title-input"
                  value={form.title}
                  onChange={(e) => set("title", e.target.value)}
                  placeholder="e.g. The Northlight, Luxury Farmhouse, Modern Courtyard Residence"
                  required
                  className="rounded-none mt-2 h-12"
                />
              )}
              <p className="text-xs text-muted-foreground mt-1">
                {form.title_mode === "auto"
                  ? "Claude names this plan based on style, size, and features when you run AI Generate."
                  : "This exact title is displayed on the marketplace and shared links."}
              </p>
            </div>
            <div>
              <Label className="overline text-muted-foreground">Style</Label>
              <Select value={form.style} onValueChange={(v) => set("style", v)}>
                <SelectTrigger data-testid="plan-style-input" className="rounded-none mt-2 h-12"><SelectValue /></SelectTrigger>
                <SelectContent>{stylesList.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {/* Iter60A — Sales channel selector. Controls which public
                marketplace(s) the plan appears on. Missing on legacy docs
                means "premium" via server-side migration. */}
            <div>
              <Label className="overline text-muted-foreground">Sales channel</Label>
              <Select value={form.sales_channel || "premium"} onValueChange={(v) => set("sales_channel", v)}>
                <SelectTrigger data-testid="plan-sales-channel-input" className="rounded-none mt-2 h-12"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="premium">Premium Browse Plans</SelectItem>
                  <SelectItem value="floor_plan">Floor Plans (instant-digital)</SelectItem>
                  <SelectItem value="both">Both marketplaces</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[10px] text-muted-foreground mt-1">
                {form.sales_channel === "floor_plan"
                  ? "Appears on /floor-plans only."
                  : form.sales_channel === "both"
                  ? "Appears on both /plans and /floor-plans (upgrade path)."
                  : "Appears on /plans (Browse Plans) only."}
              </p>
            </div>
            <div>
              <Label className="overline text-muted-foreground">Status</Label>
              <Select value={form.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger data-testid="plan-status-input" className="rounded-none mt-2 h-12"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="published">Published</SelectItem>
                  <SelectItem value="archived">Archived</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-3 mt-8">
              <Checkbox id="featured" data-testid="plan-featured-input" checked={form.featured} onCheckedChange={(v) => set("featured", !!v)} />
              <Label htmlFor="featured">Feature on home page</Label>
            </div>
            <div className="flex items-center gap-3">
              <Checkbox id="coming-soon" data-testid="plan-coming-soon-input" checked={form.coming_soon} onCheckedChange={(v) => set("coming_soon", !!v)} />
              <Label htmlFor="coming-soon">Show as “Coming Soon” on public site (must be Draft)</Label>
            </div>
            {form.coming_soon && (
              <div>
                <Label className="overline text-muted-foreground">Expected launch date</Label>
                <Input data-testid="plan-launch-date-input" type="date" value={form.launch_date || ""} onChange={(e) => set("launch_date", e.target.value)} className="rounded-none mt-2 h-12" />
              </div>
            )}
            {/* Iter45 #5 — Plan metadata grouped in a natural pairing:
                Bedrooms | Bathrooms · Stories | Garage · Sqft | Price USD · Price INR */}
            <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div><Label className="overline text-muted-foreground">Bedrooms</Label><Input data-testid="plan-bedrooms-input" type="number" value={form.bedrooms} onChange={(e) => set("bedrooms", e.target.value)} className="rounded-none mt-2 h-12" /></div>
              <div><Label className="overline text-muted-foreground">Bathrooms</Label><Input data-testid="plan-bathrooms-input" type="number" step="0.5" value={form.bathrooms} onChange={(e) => set("bathrooms", e.target.value)} className="rounded-none mt-2 h-12" /></div>
              <div><Label className="overline text-muted-foreground">Stories</Label><Input data-testid="plan-stories-input" type="number" value={form.stories} onChange={(e) => set("stories", e.target.value)} className="rounded-none mt-2 h-12" /></div>
              <div><Label className="overline text-muted-foreground">Garage bays</Label><Input data-testid="plan-garage-input" type="number" value={form.garage} onChange={(e) => set("garage", e.target.value)} className="rounded-none mt-2 h-12" /></div>
              <div><Label className="overline text-muted-foreground">Sq. Ft.</Label><Input data-testid="plan-sqft-input" type="number" value={form.sqft} onChange={(e) => set("sqft", e.target.value)} className="rounded-none mt-2 h-12" /></div>
              {/* Iter58 — Base Price (USD/INR) hidden per Centralized Currency Management.
                  Product Packages are now the single source of truth for pricing. Data is
                  preserved in the DB as a safety net for legacy plans until a future cleanup release. */}
            </div>
          </div>
        </div>

        {/* Iter48 + Iter60A — Product Packages.
            Split into two independent groups so Premium and Floor Plan pricing
            never bleed into each other. Group visibility follows `sales_channel`:
              premium    → only Premium block
              floor_plan → only Floor Plan block
              both       → both blocks visible with independent pricing        */}
        {(() => {
          const ch = form.sales_channel || "premium";
          const showPremium = ch === "premium" || ch === "both";
          const showFloorPlan = ch === "floor_plan" || ch === "both";
          const renderTierRow = (tier) => {
            const pkg = form.packages?.[tier] || { enabled: false, price_usd: 0, price_inr: 0, override_inr: false, description: "" };
            const setPkg = (patch) => setForm((f) => ({
              ...f,
              packages: { ...(f.packages || {}), [tier]: { ...pkg, ...patch } },
            }));
            const customize = isCustomizeTier(tier);
            return (
              <div key={tier} data-testid={`package-row-${tier}`}
                className={`border p-5 ${pkg.enabled ? "border-accent bg-accent/5" : "border-border bg-background"}`}>
                <div className="flex items-start gap-4">
                  <label className="flex items-center gap-3 min-w-[160px] pt-1 cursor-pointer">
                    <Checkbox
                      data-testid={`package-toggle-${tier}`}
                      checked={pkg.enabled}
                      onCheckedChange={(v) => setPkg({ enabled: !!v })}
                    />
                    <div>
                      <div className="font-display text-lg">{PACKAGE_LABEL[tier]}</div>
                      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{PACKAGE_TAGLINE[tier]}</div>
                      {customize && (
                        <div className="mt-1 text-[10px] uppercase tracking-widest text-accent">Enquiry CTA — not a paid item</div>
                      )}
                    </div>
                  </label>
                  {customize ? (
                    <div className="flex-1 text-xs text-muted-foreground pt-2">
                      Enabling this tier adds a <strong>&quot;Customize this plan&quot;</strong> button to the plan page
                      instead of a paid checkout. Submissions land in the existing Custom Design enquiry inbox.
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 flex-1">
                      <div>
                        <Label className="overline text-muted-foreground">Price (USD)</Label>
                        <Input
                          data-testid={`package-price-usd-${tier}`}
                          type="number" step="0.01" min="0"
                          disabled={!pkg.enabled}
                          value={pkg.price_usd || ""}
                          onChange={(e) => setPkg({ price_usd: Number(e.target.value) || 0 })}
                          className="rounded-none mt-2 h-11"
                        />
                        <p className="text-[10px] text-muted-foreground mt-1">Master price. INR is derived automatically.</p>
                      </div>
                      <div>
                        <div className="flex items-center justify-between">
                          <Label className="overline text-muted-foreground">Price (INR){pkg.override_inr ? "" : " · Auto"}</Label>
                          <label className="flex items-center gap-1.5 cursor-pointer text-[10px] uppercase tracking-widest">
                            <Checkbox
                              data-testid={`package-override-inr-${tier}`}
                              checked={Boolean(pkg.override_inr)}
                              disabled={!pkg.enabled}
                              onCheckedChange={(v) => setPkg({ override_inr: Boolean(v) })}
                            />
                            <span className={pkg.override_inr ? "text-primary" : "text-muted-foreground"}>Override INR</span>
                          </label>
                        </div>
                        <Input
                          data-testid={`package-price-inr-${tier}`}
                          type="number" step="1" min="0"
                          disabled={!pkg.enabled || !pkg.override_inr}
                          value={pkg.override_inr
                            ? (pkg.price_inr ?? "")
                            : (pkg.enabled && pkg.price_usd ? Math.round(Number(pkg.price_usd) * inrRate) : "")}
                          onChange={(e) => setPkg({ price_inr: Number(e.target.value) || 0 })}
                          className={`rounded-none mt-2 h-11 ${!pkg.override_inr ? "bg-neutral-50 text-muted-foreground" : ""}`}
                          placeholder={pkg.override_inr ? "Custom INR" : "Auto-calculated"}
                        />
                        <p className="text-[10px] text-muted-foreground mt-1">
                          {pkg.override_inr
                            ? "Custom INR — this exact value shows on the site regardless of rate changes."
                            : `Auto @ 1 USD = ₹${inrRate}. Updates instantly when the global rate changes.`}
                        </p>
                      </div>
                      <div className="md:col-span-2">
                        <Label className="overline text-muted-foreground">Package description (optional)</Label>
                        <Input
                          data-testid={`package-desc-${tier}`}
                          disabled={!pkg.enabled}
                          value={pkg.description || ""}
                          onChange={(e) => setPkg({ description: e.target.value })}
                          placeholder="e.g. Includes stamped drawings for permit submission"
                          className="rounded-none mt-2 h-11"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          };
          return (
            <>
              {showPremium && (
                <div className="border border-border bg-card p-8" data-testid="packages-section-premium">
                  <div className="flex items-center gap-2 mb-2">
                    <PackageIcon size={16} className="text-accent" />
                    <div className="overline text-accent">Premium House Plan Packages</div>
                  </div>
                  <p className="text-xs text-muted-foreground mb-6">
                    Prices for the <strong>Premium marketplace</strong> only. Higher tiers include everything
                    from lower tiers. CAD/Revit are delivered manually by our team after purchase.
                  </p>
                  <div className="space-y-4">{PREMIUM_TIERS.map(renderTierRow)}</div>
                </div>
              )}
              {showFloorPlan && (
                <div className="border border-border bg-card p-8" data-testid="packages-section-floor-plan">
                  <div className="flex items-center gap-2 mb-2">
                    <PackageIcon size={16} className="text-accent" />
                    <div className="overline text-accent">Floor Plan Packages</div>
                  </div>
                  <p className="text-xs text-muted-foreground mb-6">
                    Prices for the <strong>Floor Plans marketplace</strong> only — completely independent from
                    Premium pricing. <em>Customize This Plan</em> is an enquiry CTA and never enters instant-download checkout.
                  </p>
                  <div className="space-y-4">{FLOOR_PLAN_TIERS.map(renderTierRow)}</div>
                </div>
              )}
              {!showPremium && !showFloorPlan && (
                <div className="border border-dashed border-border bg-card p-8 text-center text-sm text-muted-foreground">
                  Select a sales channel above to configure pricing.
                </div>
              )}
            </>
          );
        })()}

        {/* Internal reference — CAD/Revit tracking code (admin-only, never public) */}
        <div className="border border-border bg-card p-8">
          <Label className="overline text-muted-foreground">Internal reference / file code</Label>
          <Input
            data-testid="plan-internal-reference"
            value={form.internal_reference || ""}
            onChange={(e) => set("internal_reference", e.target.value)}
            placeholder="e.g. NAS/CAD/MD-2026-047  (visible to admins only)"
            className="rounded-none mt-2 h-12"
          />
          <p className="text-[11px] text-muted-foreground mt-2">
            Optional tracking code so your team can locate the CAD/Revit source files
            on internal storage when fulfilling manual-delivery orders. Never shown to customers.
          </p>
        </div>

        {/* Iter48 — Deliverables (What's Included on PDP)
            Iter60A-QA — For the Floor Plans channel the primary deliverable
            is simply "Floor Plans" (preselected, enforced). Everything else
            lives under a collapsible "Additional deliverables" panel so the
            day-to-day workflow stays minimal. The full deliverable schema
            remains intact for future use — nothing is deleted.  */}
        {(() => {
          const channel = (form.sales_channel || "premium").toLowerCase();
          const isFP = channel === "floor_plan";
          const PRIMARY_FP = ["Floor Plans"];
          const additional = DELIVERABLE_OPTIONS.filter((o) => !PRIMARY_FP.includes(o));
          const renderCheckbox = (opt) => {
            const checked = (form.deliverables || []).includes(opt);
            return (
              <label key={opt} className="flex items-center gap-3 border border-border px-3 py-2 hover:border-foreground transition-colors cursor-pointer">
                <Checkbox
                  data-testid={`deliverable-${opt.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                  checked={checked}
                  onCheckedChange={(v) => {
                    setForm((f) => {
                      const current = f.deliverables || [];
                      return {
                        ...f,
                        deliverables: v
                          ? (current.includes(opt) ? current : [...current, opt])
                          : current.filter((d) => d !== opt),
                      };
                    });
                  }}
                />
                <span className="text-sm">{opt}</span>
              </label>
            );
          };

          if (isFP) {
            return (
              <div className="border border-border bg-card p-8" data-testid="deliverables-section">
                <div className="overline text-accent mb-2">Floor Plan Deliverable</div>
                <p className="text-xs text-muted-foreground mb-6">
                  The primary deliverable for this channel is <strong>Floor Plans</strong>. The
                  purchasable file formats (PDF / CAD / Revit / Customize) are configured under
                  Floor Plan Packages above. Add extras only if this listing also ships
                  elevations, electrical layouts, etc.
                </p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {PRIMARY_FP.map(renderCheckbox)}
                </div>
                <details className="mt-6 border border-dashed border-border" data-testid="fp-optional-deliverables">
                  <summary className="cursor-pointer px-4 py-3 text-xs uppercase tracking-widest text-muted-foreground hover:bg-muted/40">
                    Additional deliverables (optional · rare for Floor Plans)
                  </summary>
                  <div className="p-4 border-t border-border grid grid-cols-2 md:grid-cols-3 gap-3">
                    {additional.map(renderCheckbox)}
                  </div>
                </details>
              </div>
            );
          }
          return (
            <div className="border border-border bg-card p-8" data-testid="deliverables-section">
              <div className="overline text-accent mb-2">Deliverables (What&apos;s Included)</div>
              <p className="text-xs text-muted-foreground mb-6">
                Select what this plan includes. The PDP will show this list under
                &quot;What&apos;s Included&quot;, automatically filtered by the tier the customer picks.
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {DELIVERABLE_OPTIONS.map(renderCheckbox)}
              </div>
            </div>
          );
        })()}

        {/* Description */}
        <div className="border border-border bg-card p-8">
          <div className="flex items-center justify-between mb-6 flex-wrap gap-2">
            <div className="overline text-accent">Description</div>
            <div className="flex items-center gap-2">
              <Button type="button" onClick={runAi} disabled={aiLoading} data-testid="plan-ai-generate-btn" className="rounded-none bg-accent hover:bg-accent/90">
                <Sparkles size={14} className="mr-2" /> {aiLoading ? "Generating…" : "AI generate"}
              </Button>
              {(() => {
                // Iter55c — AI Vision gating. Premium: needs both a rendering
                // AND a technical drawing (renders drive marketing extraction,
                // drawings drive floor-plan extraction).
                // Iter60A-QA — Floor Plans channel gates on drawings ALONE
                // (Colored/B&W floor plan). Team should NOT need to upload a
                // marketing render just to analyze a floor plan. Vision then
                // routes the drawing to the floor-plan pipeline server-side.
                const channel = (form.sales_channel || "premium").toLowerCase();
                const hasRender = (form.images || []).length > 0;
                const drawings = form.technical_drawings || [];
                const hasFloorPlanDrawing = drawings.some((d) => {
                  // Iter60A-QA(final) — Drawing uploads persist their kind in
                  // `d.type` (not `drawing_type`). Older code paths also set
                  // `drawing_type` on the outbound payload. Check BOTH so the
                  // Vision button flips on the moment a real floor plan is
                  // added, without waiting for a save round-trip.
                  const raw = d?.drawing_type || d?.type || "";
                  const t = String(raw).toLowerCase();
                  return t === "floor_plan" || t === "floor_plan_bw" || t === "floor_plan_colored";
                });
                const hasDrawing = drawings.length > 0;
                const jobRunning = Boolean(visionJob && visionJob.status !== "completed" && visionJob.status !== "failed");
                let disabled;
                let hint = "";
                if (channel === "floor_plan") {
                  disabled = jobRunning || !hasFloorPlanDrawing;
                  if (!hasFloorPlanDrawing) hint = "Upload at least one Floor Plan drawing (Colored or B&W) to enable AI Vision.";
                } else {
                  disabled = jobRunning || !hasRender || !hasDrawing;
                  if (!hasRender && !hasDrawing) hint = "Upload at least one rendering and one floor plan to enable AI Vision.";
                  else if (!hasRender)          hint = "Upload at least one exterior/interior rendering to enable AI Vision.";
                  else if (!hasDrawing)         hint = "Upload at least one floor plan or technical drawing to enable AI Vision.";
                }
                return (
                  <div className="flex flex-col items-end gap-1">
                    <Button
                      type="button"
                      onClick={runVision}
                      disabled={disabled}
                      data-testid="plan-ai-vision-btn"
                      variant="outline"
                      className="rounded-none border-primary text-primary hover:bg-primary hover:text-primary-foreground disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Wand2 size={14} className="mr-2" />
                      {jobRunning ? "Analyzing images…" : "AI Vision Analyze"}
                    </Button>
                    {hint && (
                      <p className="text-[11px] text-muted-foreground italic max-w-[280px] text-right" data-testid="plan-ai-vision-hint">
                        {hint}
                      </p>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
          <div className="space-y-6">
            <div>
              <Label className="overline text-muted-foreground">Short description (one line)</Label>
              <Input data-testid="plan-short-desc-input" value={form.short_description} onChange={(e) => set("short_description", e.target.value)} className="rounded-none mt-2 h-12" />
            </div>
            <div>
              <Label className="overline text-muted-foreground">Long description</Label>
              <Textarea data-testid="plan-desc-input" rows={6} value={form.description} onChange={(e) => set("description", e.target.value)} className="rounded-none mt-2" />
            </div>
          </div>
        </div>

        {/* Features */}
        <div className="border border-border bg-card p-8">
          <div className="overline text-accent mb-6">Features</div>
          <div className="flex gap-2 mb-4">
            <Input data-testid="plan-feature-input" value={featureInput} onChange={(e) => setFeatureInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addFeature(); } }} placeholder="e.g. Cantilevered upper volume" className="rounded-none h-12" />
            <Button type="button" onClick={addFeature} className="rounded-none h-12 bg-primary" data-testid="plan-add-feature-btn"><Plus size={14} /></Button>
          </div>
          <ul className="space-y-2">
            {form.features.map((f) => (
              <li key={f} className="flex justify-between items-center border border-border px-4 py-2">
                <span>{f}</span>
                <button type="button" onClick={() => set("features", form.features.filter((x) => x !== f))} data-testid={`plan-remove-feature-${f.replace(/\s+/g, '-').toLowerCase()}`}><X size={14} /></button>
              </li>
            ))}
          </ul>
        </div>

        {/* Media */}
        <div className="border border-border bg-card p-8">
          <div className="flex items-center justify-between mb-6">
            <div className="overline text-accent">Media</div>
            {form.sales_channel === "floor_plan" && (
              <div className="text-[11px] uppercase tracking-widest text-accent">Colored floor plan first · Renders optional</div>
            )}
          </div>
          <div className="space-y-8">
            {/* Iter60A — For Floor Plan channel, prioritize typed drawings
                (Colored + B&W) and de-emphasize marketing renders/videos so
                the team's day-to-day workflow is: upload drawing → publish. */}
            {form.sales_channel === "floor_plan" && (
              <TypedDrawingsBlock
                form={form} set={set}
                refInput={floorFileRef}
                uploading={isUploading("technical_drawings")}
                uploadingProgress={uploadQueues.technical_drawings}
                onUpload={(files) => uploadFiles(files, "image", "technical_drawings")}
                variantStatus={variantStatus}
              />
            )}
            {form.sales_channel === "floor_plan" ? (
              <details className="border border-dashed border-border" data-testid="fp-optional-renders">
                <summary className="cursor-pointer px-4 py-3 text-xs uppercase tracking-widest text-muted-foreground hover:bg-muted/40">
                  Optional — 3D renders and marketing images
                </summary>
                <div className="p-4 border-t border-border">
                  <MediaBlock
                    label="Rendering images (optional)"
                    targetKey="images"
                    kind="image"
                    form={form} set={set}
                    input={imageInput} setInput={setImageInput} onAdd={addImage}
                    refInput={imageFileRef}
                    uploading={isUploading("images")}
                    uploadingProgress={uploadQueues.images}
                    onUpload={(files) => uploadFiles(files, "image", "images")}
                    accept="image/*"
                    testPrefix="plan-image"
                    variantStatus={variantStatus}
                  />
                </div>
              </details>
            ) : (
              <MediaBlock
                label="Rendering images (exteriors & interiors)"
                targetKey="images"
                kind="image"
                form={form} set={set}
                input={imageInput} setInput={setImageInput} onAdd={addImage}
                refInput={imageFileRef}
                uploading={isUploading("images")}
                uploadingProgress={uploadQueues.images}
                onUpload={(files) => uploadFiles(files, "image", "images")}
                accept="image/*"
                testPrefix="plan-image"
                variantStatus={variantStatus}
              />
            )}
            {form.sales_channel === "floor_plan" ? (
              <details className="border border-dashed border-border" data-testid="fp-optional-videos">
                <summary className="cursor-pointer px-4 py-3 text-xs uppercase tracking-widest text-muted-foreground hover:bg-muted/40">
                  Optional — walkthrough videos
                </summary>
                <div className="p-4 border-t border-border">
                  <VideosBlock
                    form={form} set={set}
                    refInput={videoFileRef}
                    uploading={isUploading("videos")}
                    uploadingProgress={uploadQueues.videos}
                    onUpload={(files) => uploadFiles(files, "video", "videos")}
                  />
                </div>
              </details>
            ) : (
              <VideosBlock
                form={form} set={set}
                refInput={videoFileRef}
                uploading={isUploading("videos")}
                uploadingProgress={uploadQueues.videos}
                onUpload={(files) => uploadFiles(files, "video", "videos")}
              />
            )}
            {form.sales_channel !== "floor_plan" && (
              <TypedDrawingsBlock
                form={form} set={set}
                refInput={floorFileRef}
                uploading={isUploading("technical_drawings")}
                uploadingProgress={uploadQueues.technical_drawings}
                onUpload={(files) => uploadFiles(files, "image", "technical_drawings")}
                variantStatus={variantStatus}
              />
            )}
            <div>
              <Label className="overline text-muted-foreground">PDF plan document</Label>
              <div className="mt-2 flex flex-wrap gap-2 items-center">
                <input
                  ref={pdfFileRef}
                  type="file"
                  accept="application/pdf"
                  className="hidden"
                  onChange={(e) => uploadFiles(e.target.files, "pdf", "pdf_url")}
                  data-testid="plan-pdf-file-input"
                />
                <Button
                  type="button"
                  onClick={() => pdfFileRef.current?.click()}
                  disabled={isUploading("pdf_url")}
                  className="rounded-none h-12 bg-primary"
                  data-testid="plan-upload-pdf-btn"
                >
                  <Upload size={14} className="mr-2" /> {isUploading("pdf_url") ? "Uploading…" : "Upload PDF"}
                </Button>
                <span className="text-xs text-muted-foreground">or</span>
                <Input data-testid="plan-pdf-url-input" value={form.pdf_url} onChange={(e) => set("pdf_url", e.target.value)} placeholder="Paste PDF URL…" className="rounded-none h-12 flex-1 min-w-[240px]" />
              </div>
              {form.pdf_url && (
                <div className="mt-3 flex items-center gap-2 text-sm border border-border p-3">
                  <FileText size={16} className="text-accent" />
                  <a href={form.pdf_url} target="_blank" rel="noreferrer" className="text-accent hover:underline truncate flex-1">{form.pdf_url}</a>
                  <button type="button" onClick={() => set("pdf_url", "")} data-testid="plan-remove-pdf-btn"><X size={14} /></button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {/* Iter56 — Duplicate Plan Dialog (toolbar entry) */}
      {id && (
        <DuplicatePlanDialog
          open={duplicateOpen}
          onOpenChange={setDuplicateOpen}
          plan={{ id, title: form.title, project_id: form.project_id }}
        />
      )}
    </AdminLayout>
  );
};

const VariantStatusChip = ({ status }) => {
  if (!status) {
    return (
      <span className="inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-wider bg-neutral-200 text-neutral-700 px-1.5 py-0.5 rounded-sm" data-testid="variant-status-chip-queued">
        <Loader2 size={9} className="animate-spin" /> Queued
      </span>
    );
  }
  if (status.done) {
    return (
      <span className="inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-wider bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-sm" data-testid="variant-status-chip-ready">
        <CheckCircle2 size={9} /> Ready
      </span>
    );
  }
  const pct = status.total ? Math.round((status.ready / status.total) * 100) : 0;
  return (
    <span className="inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-wider bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-sm" data-testid="variant-status-chip-processing">
      <Loader2 size={9} className="animate-spin" /> {pct}%
    </span>
  );
};

const MediaBlock = ({ label, targetKey, form, set, input, setInput, onAdd, refInput, uploading, uploadingProgress, onUpload, accept, testPrefix, variantStatus = {} }) => (
  <div>
    <Label className="overline text-muted-foreground">{label}</Label>
    <div className="mt-2 flex flex-wrap gap-2 items-center">
      <input ref={refInput} type="file" accept={accept} multiple className="hidden" onChange={(e) => onUpload(e.target.files)} data-testid={`${testPrefix}-file-input`} />
      <Button type="button" onClick={() => refInput.current?.click()} className="rounded-none h-12 bg-primary" data-testid={`${testPrefix}-upload-btn`}>
        <Upload size={14} className="mr-2" /> {uploading ? "Uploading…" : "Upload files"}
      </Button>
      {uploadingProgress && uploadingProgress.total > 0 && (
        <div className="flex items-center gap-2 text-xs" data-testid={`${testPrefix}-upload-progress`}>
          <div className="h-1.5 w-32 bg-neutral-200 overflow-hidden">
            <div className="h-full bg-primary transition-all"
                 style={{ width: `${Math.round((uploadingProgress.done / uploadingProgress.total) * 100)}%` }} />
          </div>
          <span className="font-mono text-muted-foreground">
            {uploadingProgress.done}/{uploadingProgress.total}
            {uploadingProgress.failed > 0 && <span className="text-rose-600"> · {uploadingProgress.failed} failed</span>}
          </span>
        </div>
      )}
      <span className="text-xs text-muted-foreground">or</span>
      <div className="flex gap-2 flex-1 min-w-[240px]">
        <Input data-testid={`${testPrefix}-url-input`} value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); onAdd(); } }} placeholder="Paste image URL…" className="rounded-none h-12" />
        <Button type="button" onClick={onAdd} className="rounded-none h-12 bg-primary" data-testid={`${testPrefix}-add-url-btn`}><Plus size={14} /></Button>
      </div>
    </div>
    <div className="mt-3 flex flex-wrap gap-2">
      {form[targetKey].map((img) => (
        <div key={img} className="relative w-24 h-24 border border-border">
          <img src={img} alt="" className="w-full h-full object-cover" />
          <div className="absolute bottom-0.5 left-0.5">
            <VariantStatusChip status={variantStatus[img]} />
          </div>
          <button type="button" onClick={() => set(targetKey, form[targetKey].filter((x) => x !== img))} className="absolute -top-2 -right-2 bg-background border border-border p-0.5" data-testid={`${testPrefix}-remove-btn`}><X size={12} /></button>
        </div>
      ))}
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────────────
// Videos block — MP4/WebM/MOV upload with auto-thumbnail (ffmpeg on the
// server extracts a poster frame at 1s + probes duration/dimensions).
// The architecture also accepts YouTube/Vimeo URLs via the same array
// shape (source: 'youtube' | 'vimeo') — the public gallery just needs
// to detect `source` and render the appropriate embed.
// ─────────────────────────────────────────────────────────────────────
const VideosBlock = ({ form, set, refInput, uploading, onUpload }) => {
  const items = form.videos || [];
  const [urlInput, setUrlInput] = React.useState("");

  const addUrl = () => {
    const raw = urlInput.trim();
    if (!raw) return;
    const isYouTube = /youtu\.?be/.test(raw);
    const isVimeo = /vimeo\.com/.test(raw);
    const source = isYouTube ? "youtube" : isVimeo ? "vimeo" : "file";
    set("videos", [...items, {
      url: raw,
      poster_url: "",
      source,
      mime: source === "file" ? "video/mp4" : "text/html",
      label: `Walkthrough ${items.length + 1}`,
      order: items.length,
    }]);
    setUrlInput("");
  };

  const remove = (idx) => set("videos", items.filter((_, i) => i !== idx).map((v, i) => ({ ...v, order: i })));
  const update = (idx, patch) => set("videos", items.map((v, i) => (i === idx ? { ...v, ...patch } : v)));

  const fmtDuration = (s) => {
    if (!s || Number.isNaN(s)) return "";
    const m = Math.floor(s / 60);
    const sec = Math.round(s % 60);
    return `${m}:${sec.toString().padStart(2, "0")}`;
  };
  const fmtSize = (n) => (!n && n !== 0) ? "" : (n < 1024*1024 ? `${(n/1024).toFixed(0)} KB` : `${(n/1024/1024).toFixed(1)} MB`);

  return (
    <div data-testid="videos-block">
      <div className="flex items-center justify-between mb-2 gap-3 flex-wrap">
        <div>
          <Label className="overline text-muted-foreground">Videos (walkthroughs)</Label>
          <div className="text-xs text-muted-foreground mt-1">
            MP4 / WebM / MOV up to 100 MB. Thumbnails auto-generated. Or paste a YouTube / Vimeo URL.
          </div>
        </div>
        <div>
          <input
            ref={refInput}
            type="file"
            accept="video/mp4,video/webm,video/quicktime"
            multiple
            className="hidden"
            onChange={(e) => onUpload(e.target.files)}
            data-testid="videos-file-input"
          />
          <Button
            type="button"
            onClick={() => refInput.current?.click()}
            disabled={uploading}
            className="rounded-none h-11 bg-primary"
            data-testid="videos-upload-btn"
          >
            <Upload size={14} className="mr-2" /> {uploading ? "Uploading…" : "Upload video"}
          </Button>
        </div>
      </div>

      <div className="flex gap-2 mb-3">
        <Input
          data-testid="videos-url-input"
          value={urlInput}
          onChange={(e) => setUrlInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addUrl(); } }}
          placeholder="Or paste YouTube / Vimeo URL…"
          className="rounded-none h-11"
        />
        <Button type="button" onClick={addUrl} className="rounded-none h-11 bg-primary" data-testid="videos-add-url-btn">
          <Plus size={14} />
        </Button>
      </div>

      {items.length === 0 ? (
        <div className="mt-3 border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
          No videos yet. Optional but recommended — a 30-60 second walkthrough dramatically boosts conversion.
        </div>
      ) : (
        <div className="mt-3 space-y-3">
          {items.map((v, i) => (
            <div key={`${v.url}-${i}`} className="border border-border p-3 flex gap-3 items-start" data-testid={`video-row-${i}`}>
              <div className="w-32 h-20 bg-neutral-900 flex-shrink-0 relative overflow-hidden">
                {v.poster_url ? (
                  <img src={v.poster_url} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-white/60 text-xs uppercase tracking-widest">{v.source}</div>
                )}
                <span className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <span className="w-7 h-7 rounded-full bg-white/90 flex items-center justify-center">
                    <span className="border-y-[5px] border-y-transparent border-l-[8px] border-l-neutral-900 ml-0.5" />
                  </span>
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <Input
                  value={v.label || ""}
                  onChange={(e) => update(i, { label: e.target.value })}
                  placeholder="Label shown to customers"
                  className="rounded-none h-10"
                  data-testid={`video-label-${i}`}
                />
                <div className="text-[10px] font-mono text-muted-foreground mt-1 truncate flex flex-wrap gap-3">
                  <span className="uppercase tracking-widest">{v.source}</span>
                  {v.duration_s && <span>{fmtDuration(v.duration_s)}</span>}
                  {v.size_bytes && <span>{fmtSize(v.size_bytes)}</span>}
                  <span className="truncate opacity-70">{v.url}</span>
                </div>
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={() => remove(i)}
                className="rounded-none h-10 border-rose-200 text-rose-700 hover:bg-rose-50"
                data-testid={`video-remove-${i}`}
              >
                <X size={14} />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
// object-contain to mirror how customers will see the sheet.
const TypedDrawingsBlock = ({ form, set, refInput, uploading, onUpload, variantStatus = {} }) => {
  const items = form.technical_drawings || [];
  const [meta, setMeta] = React.useState({});
  React.useEffect(() => {
    const urls = items.map((d) => d.url).filter(Boolean);
    if (urls.length === 0) { setMeta({}); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/admin/media/lookup", { params: { paths: urls.join(",") } });
        if (!cancelled) setMeta(data || {});
      } catch (e) { console.debug("[plan-form] media meta lookup failed", e); }
    })();
    return () => { cancelled = true; };
  }, [items.map((d) => d.url).join("|")]);
  const fmtBytes = (n) => {
    if (!n && n !== 0) return "";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n/1024).toFixed(0)} KB`;
    return `${(n/1024/1024).toFixed(1)} MB`;
  };
  const update = (idx, patch) => {
    const next = items.map((it, i) => (i === idx ? { ...it, ...patch } : it));
    // Iter45 #4 — Auto-label from Type. If the admin changed the Type
    // and hasn't manually edited the label yet, regenerate the default
    // label to match the new Type (e.g. "Floor Plan 3" → "Exterior Elevation 2").
    if (patch.type && !next[idx]._labelEdited) {
      const typeLabel = (DRAWING_TYPES.find((t) => t.value === patch.type) || {}).label || "Drawing";
      // Count how many other items already use this type (excluding self).
      const sameTypeCount = next.filter((it, i) => i !== idx && (it.type || "floor_plan") === patch.type).length;
      next[idx] = { ...next[idx], label: `${typeLabel} ${sameTypeCount + 1}` };
    }
    // Mark the label as user-edited the moment a real label edit lands.
    if ("label" in patch && patch.label !== undefined) {
      next[idx] = { ...next[idx], _labelEdited: true };
    }
    set("technical_drawings", next);
  };
  const remove = (idx) => {
    const next = items.filter((_, i) => i !== idx).map((it, i) => ({ ...it, order: i }));
    set("technical_drawings", next);
  };
  const move = (idx, delta) => {
    const target = idx + delta;
    if (target < 0 || target >= items.length) return;
    const next = [...items];
    [next[idx], next[target]] = [next[target], next[idx]];
    next.forEach((it, i) => (it.order = i));
    set("technical_drawings", next);
  };
  return (
    <div data-testid="typed-drawings-block">
      <div className="flex items-center justify-between mb-2">
        <div>
          <Label className="overline text-muted-foreground">Technical drawings</Label>
          <div className="text-xs text-muted-foreground mt-1">
            Assign a type &amp; label to each sheet. Customers see them grouped, with object-contain (no cropping).
          </div>
        </div>
        <div>
          <input
            ref={refInput}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={(e) => onUpload(e.target.files)}
            data-testid="typed-drawings-file-input"
          />
          <Button
            type="button"
            onClick={() => refInput.current?.click()}
            disabled={uploading}
            className="rounded-none h-11 bg-primary"
            data-testid="typed-drawings-upload-btn"
          >
            <Upload size={14} className="mr-2" /> {uploading ? "Uploading…" : "Upload drawings"}
          </Button>
        </div>
      </div>
      {items.length === 0 ? (
        <div className="mt-3 border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
          No drawings yet. Upload floor plans, elevations, sections, site plans, etc.
        </div>
      ) : (
        <div className="mt-3 space-y-3">
          {items.map((d, i) => (
            <div key={`${d.url || "new"}-${i}`} className="flex flex-col md:flex-row gap-3 border border-border p-3 bg-background" data-testid={`typed-drawing-row-${i}`}>
              <div className="w-full md:w-40 h-32 bg-neutral-100 border border-border flex-shrink-0 relative">
                <img src={d.url} alt="" className="w-full h-full object-contain p-1" draggable={false} onContextMenu={(e) => e.preventDefault()} data-protected="true" />
                <div className="absolute top-1 left-1">
                  <VariantStatusChip status={variantStatus[d.url]} />
                </div>
                {(() => {
                  const m = meta[d.url] || {};
                  if (m.unknown || (!m.width && !m.size)) return null;
                  const oversize = (m.width && m.width > 4000) || (m.size && m.size > 5 * 1024 * 1024);
                  return (
                    <div className={`absolute bottom-0 left-0 right-0 px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wider ${oversize ? "bg-amber-500/90 text-white" : "bg-black/60 text-white"}`} data-testid={`drawing-meta-${i}`}>
                      {m.width && m.height ? `${m.width}×${m.height}` : ""}{m.size ? ` · ${fmtBytes(m.size)}` : ""}{oversize ? " · LARGE" : ""}
                    </div>
                  );
                })()}
              </div>
              <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-2">
                <div>
                  <Label className="text-xs">Type</Label>
                  <Select value={d.type || "floor_plan"} onValueChange={(v) => update(i, { type: v })}>
                    <SelectTrigger data-testid={`typed-drawing-${i}-type`} className="h-10 rounded-none"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {DRAWING_TYPES.map((t) => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-2">
                  <Label className="text-xs">Label (shown to customers)</Label>
                  <Input
                    data-testid={`typed-drawing-${i}-label`}
                    value={d.label || ""}
                    onChange={(e) => update(i, { label: e.target.value })}
                    placeholder="e.g. Ground Floor Plan, North Elevation, Section A-A"
                    className="rounded-none h-10"
                  />
                </div>
                <div className="flex items-center gap-2 md:col-span-3">
                  <span className="text-xs text-muted-foreground">Order:</span>
                  <Button type="button" size="sm" variant="outline" onClick={() => move(i, -1)} disabled={i === 0} data-testid={`typed-drawing-${i}-up`}>↑</Button>
                  <Button type="button" size="sm" variant="outline" onClick={() => move(i, 1)} disabled={i === items.length - 1} data-testid={`typed-drawing-${i}-down`}>↓</Button>
                  <span className="font-mono text-xs text-muted-foreground">#{i + 1}</span>
                  <button
                    type="button"
                    onClick={() => remove(i)}
                    className="ml-auto text-xs text-red-600 hover:underline"
                    data-testid={`typed-drawing-${i}-remove`}
                  >
                    <X size={12} className="inline mr-1" /> Remove
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminPlanForm;


// ───────────────────────────────────────────────────────────────────
// Iter55f — AI Content Manager · per-field approval panel
// ───────────────────────────────────────────────────────────────────
// Renders one row per AI-generated field with a compact preview and an
// "Approve to publish" checkbox. Approvals are persisted via
// PATCH /admin/plans/{id}/ai-approvals and consumed by
// rewrite_plan_for_public → PlanDetail.jsx. Nothing publishes without
// an explicit tick + save.
const AI_APPROVAL_FIELDS = [
  { key: "short_description",    label: "Short Description",       kind: "text"  },
  { key: "description",          label: "Long Description",        kind: "text"  },
  { key: "features",             label: "Features",                 kind: "list"  },
  { key: "highlights",           label: "Architectural Highlights", kind: "list"  },
  { key: "best_for",             label: "Suitable For (buyer tags)", kind: "list" },
  { key: "suitable_family_size", label: "Suitable Family Size",     kind: "text"  },
  { key: "search_keywords",      label: "Search Keywords (SEO)",    kind: "list"  },
  { key: "suggested_tags",       label: "Suggested Tags",           kind: "list"  },
  { key: "room_schedule",        label: "Room Schedule",            kind: "rooms" },
  { key: "seo_title",            label: "SEO Title",                kind: "text"  },
  { key: "meta_description",     label: "Meta Description",         kind: "text"  },
];

const AiContentApprovalPanel = ({ planId, insights, approvals, onApprovalsChange, saving, setSaving }) => {
  const [pending, setPending] = React.useState(approvals || {});

  React.useEffect(() => { setPending(approvals || {}); }, [approvals]);

  const isDirty = React.useMemo(() => {
    for (const f of AI_APPROVAL_FIELDS) {
      const before = Boolean((approvals || {})[f.key]);
      const after = Boolean(pending[f.key]);
      if (before !== after) return true;
    }
    return false;
  }, [approvals, pending]);

  const approvedCount = AI_APPROVAL_FIELDS.filter((f) => pending[f.key]).length;

  const save = async () => {
    try {
      setSaving(true);
      const { data } = await api.patch(`/admin/plans/${planId}/ai-approvals`, { approvals: pending });
      onApprovalsChange(data.approvals || pending);
      toast.success("AI approvals saved", { description: `${AI_APPROVAL_FIELDS.filter((f) => data.approvals?.[f.key]).length} field(s) will now surface on the public page.` });
    } catch (e) {
      toast.error("Failed to save approvals", { description: e.response?.data?.detail || e.message });
    } finally {
      setSaving(false);
    }
  };

  const approveAll = () => {
    const next = {};
    AI_APPROVAL_FIELDS.forEach((f) => {
      const v = insights?.[f.key];
      if (v !== undefined && v !== null && v !== "" && !(Array.isArray(v) && v.length === 0)) {
        next[f.key] = true;
      }
    });
    setPending(next);
  };
  const rejectAll = () => setPending({});

  const renderPreview = (f) => {
    const v = insights?.[f.key];
    if (v === undefined || v === null || v === "") return <span className="italic text-muted-foreground">— (not generated)</span>;
    if (f.kind === "list") {
      if (!Array.isArray(v) || v.length === 0) return <span className="italic text-muted-foreground">— (empty)</span>;
      return (
        <div className="flex flex-wrap gap-1.5">
          {v.slice(0, 6).map((s, i) => <span key={i} className="text-[11px] px-1.5 py-0.5 bg-white border border-border">{String(s)}</span>)}
          {v.length > 6 && <span className="text-[11px] text-muted-foreground">+{v.length - 6} more</span>}
        </div>
      );
    }
    if (f.kind === "rooms") {
      if (!Array.isArray(v) || v.length === 0) return <span className="italic text-muted-foreground">— (empty)</span>;
      return <span className="text-xs text-neutral-700">{v.length} rooms detected — {v.slice(0, 3).map((r) => r.name).filter(Boolean).join(" · ")}{v.length > 3 ? "…" : ""}</span>;
    }
    // text
    const s = String(v);
    return <span className="text-xs text-neutral-800">{s.length > 180 ? s.slice(0, 180) + "…" : s}</span>;
  };

  return (
    <div className="mt-6 border-t border-primary/30 pt-5" data-testid="ai-content-manager">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <div className="overline text-primary">AI Content Manager · Publish Approvals</div>
          <div className="text-xs text-muted-foreground mt-0.5">Tick fields to approve for public display. Nothing publishes until you save.</div>
        </div>
        <div className="flex gap-2">
          <Button type="button" size="sm" variant="outline" className="rounded-none h-8 px-3" onClick={rejectAll} data-testid="ai-approvals-reject-all">Reject all</Button>
          <Button type="button" size="sm" variant="outline" className="rounded-none h-8 px-3" onClick={approveAll} data-testid="ai-approvals-approve-all">Approve all</Button>
          <Button type="button" size="sm" className="rounded-none h-8 px-4 bg-primary" onClick={save} disabled={saving || !isDirty} data-testid="ai-approvals-save">
            {saving ? <><Loader2 size={12} className="mr-1 animate-spin" />Saving…</> : `Save approvals${isDirty ? " *" : ""}`}
          </Button>
        </div>
      </div>

      <div className="text-[11px] text-muted-foreground mb-2" data-testid="ai-approvals-count">
        {approvedCount} of {AI_APPROVAL_FIELDS.length} field(s) currently ticked.
      </div>

      <div className="border border-border bg-white divide-y divide-border">
        {AI_APPROVAL_FIELDS.map((f) => {
          const v = insights?.[f.key];
          const hasValue = v !== undefined && v !== null && v !== "" && !(Array.isArray(v) && v.length === 0);
          const ticked = Boolean(pending[f.key]);
          return (
            <label key={f.key} className={`flex items-start gap-3 p-3 ${!hasValue ? "opacity-50" : ""}`} data-testid={`ai-approval-row-${f.key}`}>
              <Checkbox
                checked={ticked}
                disabled={!hasValue}
                onCheckedChange={(c) => setPending((p) => ({ ...p, [f.key]: Boolean(c) }))}
                data-testid={`ai-approval-toggle-${f.key}`}
              />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-neutral-900 flex items-center gap-2">
                  {f.label}
                  {ticked && hasValue && <span className="text-[10px] uppercase tracking-[0.16em] text-primary">Approved</span>}
                </div>
                <div className="mt-1">{renderPreview(f)}</div>
              </div>
            </label>
          );
        })}
      </div>
    </div>
  );
};
